// The house goal's celebration on the web (docs/house-week.md "The celebration"): once the week's goal
// is reached, each device shows a "Goal reached!" sheet once (its bookmark `op.goalSeen` is the newest
// week it celebrated). The open board offers each summary's week (src/views/Board.tsx); the shell
// (src/app.tsx) shows the sheet after the launch sheets, before the cheer sheets.
import { useEffect, useState } from "preact/hooks";
import { GOAL_SEEN_KEY, goalCelebrates, type HouseWeekSummary } from "../shared/house-week.ts";

let memory: string | null = null;
let pending: HouseWeekSummary | null = null;
const listeners = new Set<() => void>();

function seen(): string | null {
  try {
    return localStorage.getItem(GOAL_SEEN_KEY) ?? memory;
  } catch {
    return memory;
  }
}

function emit(): void {
  for (const fn of listeners) fn();
}

/** A board's week: queue the celebration when its goal is reached and this device has not celebrated that week. */
export function offerGoal(week: HouseWeekSummary | null | undefined): void {
  if (!week || !goalCelebrates(week, seen()) || pending?.week === week.week) return;
  pending = week;
  emit();
}

/** The celebration closed: bookmark the week. */
export function closeGoal(): void {
  if (!pending) return;
  memory = pending.week;
  try {
    localStorage.setItem(GOAL_SEEN_KEY, pending.week);
  } catch {
    /* the copy in memory holds for this open */
  }
  pending = null;
  emit();
}

/** The week to celebrate now, or null. */
export function useGoalSheet(): HouseWeekSummary | null {
  const [week, setWeek] = useState(pending);
  useEffect(() => {
    const update = () => setWeek(pending);
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return week;
}
